import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Subject, merge } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { EventDto, EventService } from '../services/event.service';
import { HomeCardCountsDto, HomeCountsService } from '../services/home-counts.service';
import { OrganizationService } from '../services/organization.service';
import { PortalNavbarComponent } from './portal-navbar.component';

interface EventSummary {
  id: number;
  name: string;
  date: string;
  teams: number;
  questions: number;
  status: string;
}

interface QuickTile {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  liveCount: string;
  staticCount: string;
  route: string;
  allowedRoles: string[];
}

@Component({
  selector: 'app-home-page',
  standalone: true,
  imports: [CommonModule, RouterModule, PortalNavbarComponent],
  template: `
    <div class="page">

      <app-portal-navbar></app-portal-navbar>

      <!-- ══════════════════ HERO ══════════════════════════════ -->
      <section class="hero">
        <div class="hero-inner">

          @if (isLoggedIn()) {

            <!-- Authenticated — left column -->
            <div class="hero-left">
              <p class="hero-eyebrow">Welcome back</p>
              @if (organizationDisplayName()) {
                <p class="hero-org">{{ organizationDisplayName() }} • {{ organizationShippingCity() || 'Unknown City' }}</p>
              }
              <h1 class="hero-title">United Team Building</h1>
              <p class="hero-sub">
                Manage your events, teams, and questions from one central hub.
              </p>
              <div class="user-pill">
                <span class="user-avatar">
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </span>
                <span>{{ userName() ?? 'Authenticated User' }}</span>
                @if (displayRoleShort(); as roleShort) {
                  <span class="user-role-pill" [title]="displayRoleFull() ?? ''">{{ roleShort }}</span>
                }
              </div>
            </div>

            <!-- Authenticated — right column (event card) -->
            <div class="hero-right">
              <div class="event-card">
                <div class="event-card-hd">
                  <span class="event-card-title">Upcoming Events</span>
                  <a routerLink="/events" class="event-card-link">View all →</a>
                </div>
                <div class="event-list">
                  @for (evt of events(); track evt.id) {
                    <div class="event-row">
                      <div class="event-row-left">
                        <span class="event-name">{{ evt.name }}</span>
                        <span class="event-date">{{ evt.date }}</span>
                      </div>
                      <div class="event-row-right">
                        <span class="event-stat">{{ evt.teams }}T</span>
                        <span class="event-stat">{{ evt.questions }}Q</span>
                        <span class="badge" [ngClass]="'badge--' + evt.status.toLowerCase()">
                          {{ evt.status }}
                        </span>
                      </div>
                    </div>
                  }
                </div>
              </div>
            </div>

          } @else {

            <!-- Unauthenticated — left column -->
            <div class="hero-left">
              <div class="hero-img" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="62" height="62"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="0.9" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                  <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
              </div>
              <h1 class="hero-title">United Team Building</h1>
              <p class="hero-sub">
                The all-in-one platform for managing team building events, trivia
                questions, scheduling, and more. Sign in to get started.
              </p>
            </div>

            <!-- Unauthenticated — right column (static event card) -->
            <div class="hero-right">
              <div class="event-card">
                <div class="event-card-hd">
                  <span class="event-card-title">Sample Events</span>
                  <span class="demo-chip">Preview</span>
                </div>
                <div class="event-list">
                  @for (evt of events(); track evt.id) {
                    <div class="event-row">
                      <div class="event-row-left">
                        <span class="event-name">{{ evt.name }}</span>
                        <span class="event-date">{{ evt.date }}</span>
                      </div>
                      <div class="event-row-right">
                        <span class="event-stat">{{ evt.teams }}T</span>
                        <span class="event-stat">{{ evt.questions }}Q</span>
                        <span class="badge" [ngClass]="'badge--' + evt.status.toLowerCase()">
                          {{ evt.status }}
                        </span>
                      </div>
                    </div>
                  }
                </div>
                <p class="event-card-cta">Sign in to manage your events.</p>
              </div>
            </div>

          }
        </div>
      </section>

      <!-- ══════════════════ QUICK ACCESS TILES ═══════════════ -->
      <section class="tiles-section">
        <div class="tiles-inner">
          <div class="tiles-hd">
            <h2 class="tiles-title">Quick Access</h2>
            @if (!isLoggedIn()) {
              <span class="demo-chip">Demo Preview</span>
            }
          </div>

          <div class="tiles-grid">
            @for (tile of filteredTiles(); track tile.id) {
              <div class="tile" [routerLink]="isLoggedIn() ? tile.route : null">
                <div class="tile-top">
                  <div class="tile-icon" [ngClass]="'tile-icon--' + tile.color">
                    @switch (tile.icon) {
                      @case ('calendar') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8"  y1="2" x2="8"  y2="6"/>
                          <line x1="3"  y1="10" x2="21" y2="10"/>
                        </svg>
                      }
                      @case ('users') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                          <circle cx="9" cy="7" r="4"/>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                        </svg>
                      }
                      @case ('help') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                          <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                      }
                      @case ('clock') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                      }
                      @case ('sliders') {
                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                          <line x1="4"  y1="21" x2="4"  y2="14"/>
                          <line x1="4"  y1="10" x2="4"  y2="3"/>
                          <line x1="12" y1="21" x2="12" y2="12"/>
                          <line x1="12" y1="8"  x2="12" y2="3"/>
                          <line x1="20" y1="21" x2="20" y2="16"/>
                          <line x1="20" y1="12" x2="20" y2="3"/>
                          <line x1="1"  y1="14" x2="7"  y2="14"/>
                          <line x1="9"  y1="8"  x2="15" y2="8"/>
                          <line x1="17" y1="16" x2="23" y2="16"/>
                        </svg>
                      }
                    }
                  </div>
                  <div class="tile-chevron">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>
                </div>
                <h3 class="tile-name">{{ tile.title }}</h3>
                <p class="tile-desc">{{ tile.description }}</p>
                <span class="tile-count">{{ getTileCountText(tile) }}</span>
              </div>
            }
          </div>
        </div>
      </section>

      <!-- ══════════════════ FOOTER ════════════════════════════ -->
      <footer class="footer">
        <div class="footer-inner">
          <div class="footer-brand">
            <div class="footer-logo">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <span class="footer-name">United Team Building</span>
          </div>
          <p class="footer-copy">&copy; {{ year }} United Team Building. All rights reserved.</p>
        </div>
      </footer>

    </div>
  `,
  styles: [`

    /* ── Page shell ─────────────────────────────────────────── */
    .page {
      display: flex;
      flex-direction: column;
      min-height: 100vh;
    }

    /* ── Hero ───────────────────────────────────────────────── */
    .hero {
      background:
        radial-gradient(
          ellipse at 15% 60%,
          color-mix(in srgb, var(--color-accent) 9%, transparent) 0%,
          transparent 58%
        ),
        linear-gradient(
          155deg,
          var(--color-bg-start) 0%,
          color-mix(in srgb, var(--color-bg-end) 88%, var(--color-accent) 12%) 100%
        );
      border-bottom: 1px solid var(--color-border);
      padding: 3.5rem 1.5rem 4rem;
    }

    .hero-inner {
      align-items: center;
      display: grid;
      gap: 2rem;
      grid-template-columns: 3fr 2fr;
      margin: 0 auto;
      max-width: 1280px;
    }

    .hero-left {
      display: flex;
      flex-direction: column;
    }

    .hero-eyebrow {
      color: var(--color-accent-soft);
      font-size: 0.78rem;
      font-weight: 600;
      letter-spacing: 0.13em;
      margin: 0 0 0.4rem;
      text-transform: uppercase;
    }

    .hero-title {
      color: var(--color-text);
      font-size: clamp(1.75rem, 3vw, 2.5rem);
      font-weight: 800;
      line-height: 1.15;
      margin: 0 0 0.65rem;
    }

    .hero-org {
      color: var(--color-text);
      font-size: 0.9rem;
      font-weight: 600;
      margin: 0 0 0.7rem;
    }

    .hero-sub {
      color: var(--color-muted);
      font-size: 0.93rem;
      line-height: 1.65;
      margin: 0 0 1.5rem;
      max-width: 46ch;
    }

    /* User pill */
    .user-pill {
      align-items: center;
      align-self: flex-start;
      background: color-mix(in srgb, var(--color-accent) 15%, transparent);
      border: 1px solid color-mix(in srgb, var(--color-accent) 28%, transparent);
      border-radius: 9999px;
      color: var(--color-accent-soft);
      display: flex;
      font-size: 0.82rem;
      font-weight: 600;
      gap: 0.45rem;
      padding: 0.28rem 0.75rem 0.28rem 0.38rem;
    }

    .user-avatar {
      align-items: center;
      background: color-mix(in srgb, var(--color-accent) 26%, transparent);
      border-radius: 50%;
      color: var(--color-accent-soft);
      display: flex;
      height: 22px;
      justify-content: center;
      width: 22px;
    }

    .user-role-pill {
      background: color-mix(in srgb, var(--color-accent) 25%, transparent);
      border: 1px solid color-mix(in srgb, var(--color-accent) 34%, transparent);
      border-radius: 9999px;
      color: var(--color-accent-soft);
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.02em;
      padding: 0.1rem 0.45rem;
      text-transform: uppercase;
    }

    /* Hero image placeholder (unauthenticated) */
    .hero-img {
      align-items: center;
      background: color-mix(in srgb, var(--color-surface-2) 55%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      color: color-mix(in srgb, var(--color-accent) 55%, var(--color-muted));
      display: flex;
      height: 148px;
      justify-content: center;
      margin-bottom: 1.5rem;
      width: 100%;
    }

    /* Event card */
    .event-card {
      backdrop-filter: blur(10px);
      background: color-mix(in srgb, var(--color-surface-2) 80%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .event-card-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 0.8rem 1rem;
    }

    .event-card-title {
      color: var(--color-text);
      font-size: 0.83rem;
      font-weight: 700;
    }

    .event-card-link {
      color: var(--color-accent-soft);
      font-size: 0.75rem;
      text-decoration: none;
      transition: color 0.15s;
    }

    .event-card-link:hover { color: var(--color-accent); }

    .demo-chip {
      background: color-mix(in srgb, var(--color-accent) 13%, transparent);
      border: 1px solid color-mix(in srgb, var(--color-accent) 22%, transparent);
      border-radius: 9999px;
      color: var(--color-muted);
      font-size: 0.65rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      padding: 0.18rem 0.5rem;
      text-transform: uppercase;
    }

    .event-list { padding: 0.2rem 0; }

    .event-row {
      align-items: center;
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent);
      display: flex;
      gap: 0.5rem;
      justify-content: space-between;
      padding: 0.6rem 1rem;
    }

    .event-row:last-child { border-bottom: none; }

    .event-row-left {
      display: flex;
      flex-direction: column;
      gap: 0.12rem;
      min-width: 0;
    }

    .event-name {
      color: var(--color-text);
      font-size: 0.8rem;
      font-weight: 600;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .event-date {
      color: var(--color-muted);
      font-size: 0.72rem;
    }

    .event-row-right {
      align-items: center;
      display: flex;
      flex-shrink: 0;
      gap: 0.35rem;
    }

    .event-stat {
      color: var(--color-muted);
      font-size: 0.7rem;
    }

    /* Status badges */
    .badge {
      border-radius: 9999px;
      font-size: 0.64rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      padding: 0.16rem 0.45rem;
    }

    .badge--upcoming {
      background: color-mix(in srgb, var(--color-accent) 17%, transparent);
      color: var(--color-accent-soft);
    }

    .badge--scheduled {
      background: color-mix(in srgb, #f59e0b 14%, transparent);
      color: #fbbf24;
    }

    .badge--live {
      background: color-mix(in srgb, #22c55e 14%, transparent);
      color: #4ade80;
    }

    .badge--completed {
      background: color-mix(in srgb, var(--color-muted) 14%, transparent);
      color: var(--color-muted);
    }

    .event-card-cta {
      border-top: 1px solid var(--color-border);
      color: var(--color-muted);
      font-size: 0.76rem;
      margin: 0;
      padding: 0.6rem 1rem;
      text-align: center;
    }

    /* ── Quick Access Tiles ─────────────────────────────────── */
    .tiles-section {
      flex: 1;
      padding: 3rem 1.5rem;
    }

    .tiles-inner {
      margin: 0 auto;
      max-width: 1280px;
    }

    .tiles-hd {
      align-items: center;
      display: flex;
      gap: 0.7rem;
      margin-bottom: 1.4rem;
    }

    .tiles-title {
      color: var(--color-text);
      font-size: 1.15rem;
      font-weight: 700;
      margin: 0;
    }

    .tiles-grid {
      display: grid;
      gap: 1rem;
      grid-template-columns: repeat(4, 1fr);
    }

    .tile {
      background: color-mix(in srgb, var(--color-surface-2) 78%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
      padding: 1.2rem;
      transition: background 0.2s, border-color 0.2s, transform 0.18s;
    }

    .tile:hover {
      background: color-mix(in srgb, var(--color-surface-2) 92%, var(--color-accent) 8%);
      border-color: color-mix(in srgb, var(--color-accent) 35%, var(--color-border));
      transform: translateY(-3px);
    }

    .tile-top {
      align-items: flex-start;
      display: flex;
      justify-content: space-between;
      margin-bottom: 0.25rem;
    }

    .tile-icon {
      align-items: center;
      border-radius: 0.6rem;
      display: flex;
      height: 44px;
      justify-content: center;
      width: 44px;
    }

    .tile-icon--blue   { background: color-mix(in srgb, #3b82f6 16%, transparent); color: #60a5fa; }
    .tile-icon--cyan   { background: color-mix(in srgb, #06b6d4 16%, transparent); color: #22d3ee; }
    .tile-icon--indigo { background: color-mix(in srgb, #6366f1 16%, transparent); color: #818cf8; }
    .tile-icon--sky    { background: color-mix(in srgb, #0ea5e9 16%, transparent); color: #38bdf8; }
    .tile-icon--violet { background: color-mix(in srgb, #8b5cf6 16%, transparent); color: #a78bfa; }

    .tile-chevron {
      color: var(--color-muted);
      opacity: 0.4;
      transition: color 0.15s, opacity 0.15s;
    }

    .tile:hover .tile-chevron {
      color: var(--color-accent-soft);
      opacity: 1;
    }

    .tile-name {
      color: var(--color-text);
      font-size: 0.93rem;
      font-weight: 700;
      margin: 0;
    }

    .tile-desc {
      color: var(--color-muted);
      font-size: 0.78rem;
      line-height: 1.5;
      margin: 0;
    }

    .tile-count {
      background: color-mix(in srgb, var(--color-surface-1) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 9999px;
      color: var(--color-accent-soft);
      display: inline-block;
      font-size: 0.7rem;
      font-weight: 600;
      margin-top: 0.2rem;
      padding: 0.14rem 0.5rem;
    }

    /* ── Footer ─────────────────────────────────────────────── */
    .footer {
      background: color-mix(in srgb, var(--color-surface-1) 90%, transparent);
      border-top: 1px solid var(--color-border);
      padding: 1.4rem 1.5rem;
    }

    .footer-inner {
      align-items: center;
      display: flex;
      flex-direction: column;
      gap: 0.45rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    .footer-brand {
      align-items: center;
      display: flex;
      gap: 0.5rem;
    }

    .footer-logo {
      align-items: center;
      background: color-mix(in srgb, var(--color-accent) 18%, transparent);
      border: 1px solid color-mix(in srgb, var(--color-accent) 22%, transparent);
      border-radius: 0.45rem;
      color: var(--color-accent-soft);
      display: flex;
      height: 32px;
      justify-content: center;
      width: 32px;
    }

    .footer-name {
      color: var(--color-text);
      font-size: 0.88rem;
      font-weight: 700;
    }

    .footer-copy {
      color: var(--color-muted);
      font-size: 0.73rem;
      margin: 0;
    }

    /* ── Responsive ─────────────────────────────────────────── */
    @media (max-width: 900px) {
      .hero-inner      { grid-template-columns: 1fr; }
      .tiles-grid      { grid-template-columns: repeat(2, 1fr); }
    }

    @media (max-width: 520px) {
      .tiles-grid      { grid-template-columns: 1fr; }
    }
  `]
})
export class HomePageComponent implements OnInit, OnDestroy {
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly authService          = inject(AuthService);
  private readonly eventService         = inject(EventService);
  private readonly homeCountsService    = inject(HomeCountsService);
  private readonly organizationService  = inject(OrganizationService);
  private readonly destroying$          = new Subject<void>();

  private readonly demoEvents: EventSummary[] = [
    { id: 1, name: 'Q4 Team Challenge', date: 'Dec 15, 2025', teams: 6, questions: 40, status: 'Upcoming' },
    { id: 2, name: 'Holiday Trivia Night', date: 'Dec 22, 2025', teams: 4, questions: 30, status: 'Upcoming' },
    { id: 3, name: 'Annual Leadership Summit', date: 'Jan 10, 2026', teams: 8, questions: 50, status: 'Scheduled' },
  ];

  readonly isLoggedIn = signal(false);
  readonly userName   = signal<string | null>(null);
  readonly organizationDisplayName = signal<string | null>(null);
  readonly organizationShippingCity = signal<string | null>(null);
  readonly year       = new Date().getFullYear();
  readonly roles      = signal<string[]>([]);

  readonly events = signal<EventSummary[]>([...this.demoEvents]);
  readonly homeCounts = signal<HomeCardCountsDto | null>(null);

  readonly displayRoleFull = computed(() => {
    const roles = this.roles();
    if (roles.includes('SiteAdmin')) return 'SiteAdmin';
    if (roles.includes('OrgAdmin')) return 'OrgAdmin';
    if (roles.includes('EventPlanner')) return 'EventPlanner';
    if (roles.includes('TeamMember')) return 'TeamMember';
    return null;
  });

  readonly displayRoleShort = computed(() => {
    const fullRole = this.displayRoleFull();
    if (fullRole === 'SiteAdmin') return 'Site';
    if (fullRole === 'OrgAdmin') return 'Org';
    if (fullRole === 'EventPlanner') return 'Planner';
    if (fullRole === 'TeamMember') return 'Member';
    return null;
  });

  readonly filteredTiles = computed(() => {
    if (!this.isLoggedIn()) {
      return this.tiles;
    }

    const roles = this.roles();
    return this.tiles.filter(tile => tile.allowedRoles.some(role => roles.includes(role)));
  });

  readonly tiles: QuickTile[] = [
    {
      id: 'events',      title: 'Events',
      description: 'Create and manage team building events.',
      icon: 'calendar', color: 'blue',
      liveCount: '3 active',      staticCount: '3 sample',
      route: '/events',
      allowedRoles: ['SiteAdmin', 'OrgAdmin', 'EventPlanner']
    },
    {
      id: 'teams',       title: 'Teams',
      description: 'Organize participants into competing teams.',
      icon: 'users',    color: 'cyan',
      liveCount: '12 teams',     staticCount: '6 sample',
      route: '/teams',
      allowedRoles: ['SiteAdmin', 'OrgAdmin', 'EventPlanner']
    },
    {
      id: 'questions',   title: 'Questions',
      description: 'Build and curate your question catalog.',
      icon: 'help',     color: 'indigo',
      liveCount: '240 questions', staticCount: '40 sample',
      route: '/questions',
      allowedRoles: ['SiteAdmin', 'OrgAdmin', 'EventPlanner']
    },
    {
      id: 'scheduling',  title: 'Scheduling',
      description: 'Plan and schedule events on the calendar.',
      icon: 'clock',    color: 'sky',
      liveCount: '2 upcoming',   staticCount: '1 sample',
      route: '/scheduling',
      allowedRoles: ['SiteAdmin', 'OrgAdmin', 'EventPlanner']
    },
    {
      id: 'game-preferences', title: 'Game Preferences',
      description: 'Configure game rules, scoring, and behaviour.',
      icon: 'sliders',  color: 'violet',
      liveCount: 'Configure',    staticCount: 'Configure',
      route: '/game-preferences',
      allowedRoles: ['SiteAdmin', 'EventPlanner']
    },
  ];

  ngOnInit(): void {
    void this.checkAuth();
    merge(
      this.msalBroadcastService.inProgress$.pipe(filter(s => s === InteractionStatus.None)),
      this.authService.simStateChanged$
    ).pipe(
      takeUntil(this.destroying$)
    ).subscribe(() => {
      void this.checkAuth();
    });
  }

  private async checkAuth(): Promise<void> {
    const account = this.authService.getActiveAccount();
    this.isLoggedIn.set(account !== null);
    this.userName.set(account?.name ?? null);

    if (account) {
      const roles = await this.authService.getRolesAsync();
      this.roles.set(roles);
      this.loadHomeCounts();

      const isSiteAdmin = roles.includes('SiteAdmin');
      if (isSiteAdmin) {
        this.organizationDisplayName.set(null);
        this.organizationShippingCity.set(null);
        this.loadUpcomingEvents(null);
        return;
      }

      this.loadMyOrganizationAndEvents();
      return;
    }

    this.roles.set([]);
    this.homeCounts.set(null);
    this.organizationDisplayName.set(null);
    this.organizationShippingCity.set(null);
    this.events.set([...this.demoEvents]);
  }

  private loadHomeCounts(): void {
    this.homeCountsService.getCounts().subscribe({
      next: counts => this.homeCounts.set(counts),
      error: () => this.homeCounts.set(null)
    });
  }

  private loadMyOrganizationAndEvents(): void {
    this.organizationService.getMyOrganization().subscribe({
      next: org => {
        this.organizationDisplayName.set(org.displayName || org.legalName || null);
        this.organizationShippingCity.set(org.shippingCity || null);
        this.loadUpcomingEvents(org.id);
      },
      error: () => {
        this.organizationDisplayName.set(null);
        this.organizationShippingCity.set(null);
        this.loadUpcomingEvents(null);
      }
    });
  }

  getTileCountText(tile: QuickTile): string {
    if (!this.isLoggedIn()) {
      return tile.staticCount;
    }

    const counts = this.homeCounts();
    if (!counts) {
      return 'Loading…';
    }

    switch (tile.id) {
      case 'events':
        return `${counts.events} events`;
      case 'teams':
        return `${counts.teams} teams`;
      case 'questions':
        return `${counts.questions} questions`;
      case 'scheduling':
        return `${counts.scheduling} upcoming`;
      default:
        return tile.liveCount;
    }
  }

  private loadUpcomingEvents(organizationId: number | null): void {
    this.eventService.getAll().subscribe({
      next: (eventDtos: EventDto[]) => {
        const scoped = organizationId
          ? eventDtos.filter(e => e.organizationId === organizationId)
          : eventDtos;

        const upcoming = scoped
          .filter(e => (e.status ?? '').toLowerCase() !== 'completed')
          .sort((a, b) => new Date(a.scheduledDateTime).getTime() - new Date(b.scheduledDateTime).getTime())
          .slice(0, 3)
          .map(e => this.mapEventSummary(e));

        this.events.set(upcoming);
      },
      error: () => {
        this.events.set([]);
      }
    });
  }

  private mapEventSummary(dto: EventDto): EventSummary {
    return {
      id: dto.id,
      name: dto.name,
      date: new Date(dto.scheduledDateTime).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      teams: 0,
      questions: 0,
      status: dto.status
    };
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }
}
